import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { InstantSearch, SearchBox, Hits, Configure, Pagination, RefinementList } from "react-instantsearch";
import "instantsearch.css/themes/satellite.css";
import searchClient from "../../../utils/algolia/algoliaClient";
import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import CustomFilterWidget from "../../filter/filter-widget";
import CustomFilterButton from "../../filter/filter-button";
import CustomHighlight from "../custom-highlight";
import PropTypes from "prop-types";
import "./global-search.scss";
import { useMuiScreenSizes } from "../../../hooks/useMuiScreenSizes";
import { handleTrackReportClick } from "../../../utils/google-analytics";
import { isExternalLink } from "../../../utils/utils";

const globalSearchIndexName = process.env.REACT_APP_ALGOLIA_GLOBAL_SEARCH_INDEX;

function GlobalSearchResult(props) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryParams = new URLSearchParams(location.search);
  const query = queryParams.get("query");
  const { isScreenSmall, isScreenMedium } = useMuiScreenSizes();
  const [initialUiState, setInitialUiState] = useState({
    [globalSearchIndexName]: {
      query: "",
      refinementList: {}
    }
  });
  const [isFilterOpen, setIsFilterOpen] = useState(!isScreenMedium);

  useEffect(() => {
    const categoryRefinement = searchParams.getAll("category");

    const refinementList = {};
    if (categoryRefinement.length > 0) {
      refinementList.index = categoryRefinement;
    }

    setInitialUiState({
      [globalSearchIndexName]: {
        query,
        refinementList
      }
    });
  }, [searchParams]);

  const filterColumns = [
    {
      filterColumnName: "Filter By Categories",
      refinementListComponent: (
        <RefinementList attribute="index" limit={15} showMore showMoreLimit={100} sortBy={["name:asc"]} />
      )
    },
    {
      filterColumnName: "Filter By Keywords",
      refinementListComponent: (
        <RefinementList attribute="keywords" limit={15} showMore showMoreLimit={100} sortBy={["name:asc"]} />
      )
    }
  ];

  const GlobalSearchHit = ({ hit }) => {
    if (!hit) return null;

    return (
      <div className="d-block px-2">
        <MuiLink
          variant="h6"
          underline="hover"
          component={Link}
          to={hit.url}
          target={isExternalLink(hit.url) ? "_blank" : "_self"}
          sx={{ lineHeight: 1.5 }}
          onClick={() => {
            if (hit.index === "Report Document") {
              handleTrackReportClick({
                event: "report_download_global_search_page",
                reportId: hit.objectID,
                reportName: hit.title,
                reportDivision: (hit.tags?.[0]?.replace(/^Division: /, '') || ""),
                origin: "Global Search Page"
              });
            }
          }}
        >
          <CustomHighlight attribute="title" hit={hit} />
        </MuiLink>

        {hit.tags && hit.tags.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {hit.tags.map((division, index) => (
              <Chip
                key={index}
                variant="outlined"
                size="small"
                label={division}
                sx={{ backgroundColor: "#f4f8fc", border: "1px solid #c3d7ec" }}
              />
            ))}
          </div>
        )}

        <Typography
          className={`hit-description mt-${hit.tags && hit.tags.length > 0 ? 2 : 1}`}
          variant="body1"
          color="textSecondary"
        >
          <CustomHighlight attribute="chunk" hit={hit} />
        </Typography>
      </div>
    );
  };

  GlobalSearchHit.propTypes = {
    hit: PropTypes.object.isRequired
  };

  return (
    <div className="container mt-5 mb-5">
      <h1 className="display-5 text-center mb-lg-4 pb-4 text-break">ATC Website Search</h1>

      <InstantSearch
        indexName={globalSearchIndexName}
        searchClient={searchClient}
        initialUiState={initialUiState}
        key={JSON.stringify(initialUiState)}
      >
        <Configure hitsPerPage={10} />

        <div className="faq-searchbox d-flex justify-content-center py-2">
          <SearchBox placeholder="Search ATC Website" searchAsYouType />
        </div>

        {/* Filter Button */}
        <div className="pt-4 pb-2">
          <CustomFilterButton isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} {...props} />
        </div>

        {/* Filter Widget & Results */}
        <div className={`d-flex ${isFilterOpen ? "gap-4" : "gap-0"}`}>
          <CustomFilterWidget
            filterColumns={filterColumns}
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            isDesktop={!isScreenMedium}
            {...props}
          />

          <Box
            sx={{
              flexBasis: 0,
              flexGrow: 1,
              transition: "width 0.3s ease"
            }}
          >
            <Hits hitComponent={({ hit }) => <GlobalSearchHit hit={hit} />} />
          </Box>
        </div>

        <Pagination
          className="d-flex justify-content-center mt-4 mx-4"
          padding={isScreenSmall ? 1 : isScreenMedium ? 2 : 3}
        />
      </InstantSearch>
    </div>
  );
}

export default GlobalSearchResult;
